import * as React from "react";
import {useEffect} from "react";
import {PubSub} from "./components/PubSub";
import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextareaAutosize} from "@mui/material";
import Button from "@mui/material/Button";
import {makeStyles} from "@mui/styles";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin, UserSimplified} from "./atoms/CurrentUser";

type ErrorHandlerProps = {}

type ErrorState = {
   title: string;
   message: string;
   showDetails: boolean;
} | null;

const parseError = (args: any): ErrorState => {
   const rawJson = JSON.stringify(args, null, '\t');

   // Check for audit note requirement error
   const isAuditNoteError = Array.isArray(args) && args.some((err: any) =>
      err.message?.includes('requires an audit note')
   );

   if (isAuditNoteError) {
      return {
         title: 'Settings are locked',
         message: 'These settings require an audit note before changes can be made. Please create an audit note first.',
         showDetails: false
      };
   }

   return {
      title: 'Unexpected error occured',
      message: rawJson,
      showDetails: true
   };
};

export const ErrorHandler: React.FunctionComponent<ErrorHandlerProps> = (props) => {
   const [error, setError] = React.useState<ErrorState>(null);
   const isAdmin = useRecoilValue(CurrentUserIsAdmin);
   const isSimplified = useRecoilValue(UserSimplified);
   const showTechnicalDetails = isAdmin && !isSimplified;
   const classes = useStyles();

   useEffect(() => {
      PubSub.on('errors', (args) => {
         setError(parseError(args));
      });
   }, [])

   const handleClose= () => {
      setError(null);
   }

   const renderErrorContent = () => {
      if (!error) return null;

      // For special errors (like audit note), show the custom message
      if (!error.showDetails) {
         return <div style={{padding: '0 24px 16px'}}>{error.message}</div>;
      }

      // For regular errors, show details only to admins without simplified UI
      if (showTechnicalDetails) {
         return <TextareaAutosize className={classes.editor} value={error.message}/>;
      }

      // Non-admin users get a friendly message
      return (
         <DialogContent>
            <DialogContentText>
               We were unable to complete your request. Please try again later.
               If the problem persists, please contact support.
            </DialogContentText>
         </DialogContent>
      );
   };

   return <div>
      {error && <Dialog fullWidth={true} maxWidth={showTechnicalDetails && error.showDetails ? "md" : "sm"} open={!!error} onClose={handleClose}>
         <DialogTitle id="error-dialog-title">
            {showTechnicalDetails || !error.showDetails ? error.title : 'Something went wrong'}
         </DialogTitle>
         {renderErrorContent()}
         <DialogActions>
            <Button onClick={handleClose} color="primary">
               Close
            </Button>
         </DialogActions>
      </Dialog>}
      {props.children}
   </div>
}

const useStyles = makeStyles({
   editor: {
      width: '100%',
      backgroundColor: '#f0f0f0'
   }
});